import { INSTITUTIONAL_TYPES } from './constants';

/**
 * Compute per-broker institutional flow analysis for each stock.
 * Uses the ENTIRE uploaded history as the basis for positions and signals.
 *
 * Signal A (LARGE LOW-COST): Institution has significant net accumulation at avg price below current
 * Signal B (MULTI-INST BUY): 3+ institutions are net accumulators across the full dataset
 */
export function computeInstitutionalFlow(brokerData, priceData, brokerTags) {
  const bySymbol = {};
  brokerData.forEach(r => {
    if (!bySymbol[r.symbol]) bySymbol[r.symbol] = [];
    bySymbol[r.symbol].push(r);
  });

  // Get latest price per symbol (most recent date)
  const latestPrice = {};
  priceData.forEach(r => {
    if (!latestPrice[r.symbol] || r.date > latestPrice[r.symbol].date) {
      latestPrice[r.symbol] = r;
    }
  });
  // Fallback: derive from broker data averages
  brokerData.forEach(r => {
    if (!latestPrice[r.symbol] || r.date > (latestPrice[r.symbol].date || '')) {
      const ave = r.buyAve || r.sellAve || 0;
      if (ave > 0 && (!latestPrice[r.symbol] || r.date > latestPrice[r.symbol].date)) {
        latestPrice[r.symbol] = { close: ave, date: r.date };
      }
    }
  });

  const results = {};

  for (const [symbol, rows] of Object.entries(bySymbol)) {
    const currentPrice = latestPrice[symbol]?.close || 0;
    const allDates = [...new Set(rows.map(r => r.date))].sort();
    const firstDate = allDates[0];
    const lastDate = allDates[allDates.length - 1];

    // ── Aggregate per broker across ALL dates (full history) ──
    const brokerMap = {};
    rows.forEach(r => {
      const type = getBrokerType(r.broker, brokerTags);
      const key = r.broker;
      if (!brokerMap[key]) {
        brokerMap[key] = {
          broker: r.broker,
          type,
          totalBuyVol: 0,
          totalBuyAmt: 0,
          totalSellVol: 0,
          totalSellAmt: 0,
          dailyNet: {},     // date -> net shares that day
          dailyBuyVol: {},  // date -> buy vol that day
          dailySellVol: {}, // date -> sell vol that day
        };
      }
      const b = brokerMap[key];
      b.totalBuyVol += r.buyVol || 0;
      b.totalBuyAmt += r.buyAmt || 0;
      b.totalSellVol += r.sellVol || 0;
      b.totalSellAmt += r.sellAmt || 0;
      const net = (r.buyVol || 0) - (r.sellVol || 0);
      b.dailyNet[r.date] = (b.dailyNet[r.date] || 0) + net;
      b.dailyBuyVol[r.date] = (b.dailyBuyVol[r.date] || 0) + (r.buyVol || 0);
      b.dailySellVol[r.date] = (b.dailySellVol[r.date] || 0) + (r.sellVol || 0);
    });

    // Total volume across all brokers (for % calculations)
    const totalSymbolBuyVol = Object.values(brokerMap).reduce((s, x) => s + x.totalBuyVol, 0);

    // ── Compute derived fields per broker ──
    const positions = Object.values(brokerMap).map(b => {
      // Full-history cumulative position
      const netPosition = b.totalBuyVol - b.totalSellVol;
      // Full-history volume-weighted average price
      const avgBuyPrice = b.totalBuyVol > 0 ? b.totalBuyAmt / b.totalBuyVol : 0;
      const avgSellPrice = b.totalSellVol > 0 ? b.totalSellAmt / b.totalSellVol : 0;

      // Weekly trend (for display, not signal gating)
      const weeklyNet = computeWeeklyNet(b.dailyNet, allDates);
      const buyWeeks = weeklyNet.filter(w => w.net > 0).length;
      const sellWeeks = weeklyNet.filter(w => w.net < 0).length;
      const totalWeeks = Math.max(weeklyNet.length, 1);

      // Activity days: how many days this broker was active
      const activeBuyDays = Object.values(b.dailyBuyVol).filter(v => v > 0).length;
      const activeSellDays = Object.values(b.dailySellVol).filter(v => v > 0).length;
      const activeDays = new Set([...Object.keys(b.dailyBuyVol), ...Object.keys(b.dailySellVol)]).size;

      // Trend based on full history net + weekly direction
      let trend = 'FLAT';
      if (netPosition > 0 && (buyWeeks >= sellWeeks || totalWeeks <= 1)) trend = 'ACCUMULATING';
      else if (netPosition < 0 && (sellWeeks >= buyWeeks || totalWeeks <= 1)) trend = 'DISTRIBUTING';
      else if (netPosition > 0) trend = 'ACCUMULATING';
      else if (netPosition < 0) trend = 'DISTRIBUTING';

      // ── Signal sub-flags (per broker) ──
      // Buying at low price: full-history avg buy is below current market
      const buyingAtLowPrice = avgBuyPrice > 0 && currentPrice > 0 && avgBuyPrice < currentPrice;

      // Large position: net > 2% of total symbol flow
      const positionPct = totalSymbolBuyVol > 0 ? netPosition / totalSymbolBuyVol : 0;
      const hasLargePosition = netPosition > 0 && Math.abs(positionPct) > 0.02;

      // Consistent buyer: net positive overall (the whole history IS the evidence)
      const isConsistentBuyer = netPosition > 0 && b.totalBuyVol > b.totalSellVol * 1.1;

      // Consecutive buy weeks (bonus metric, not required for signal)
      const consecutiveBuyWeeks = countConsecutiveEnd(weeklyNet, 'buy');

      return {
        broker: b.broker,
        type: b.type,
        isInstitutional: INSTITUTIONAL_TYPES.includes(b.type),
        // Full-history totals
        totalBuyVol: b.totalBuyVol,
        totalSellVol: b.totalSellVol,
        netPosition,
        avgBuyPrice: Math.round(avgBuyPrice * 10000) / 10000,
        avgSellPrice: Math.round(avgSellPrice * 10000) / 10000,
        totalBuyAmt: Math.round(b.totalBuyAmt),
        totalSellAmt: Math.round(b.totalSellAmt),
        // Activity
        activeDays,
        activeBuyDays,
        activeSellDays,
        // Weekly detail (for charts)
        weeklyNet,
        buyWeeks,
        sellWeeks,
        consecutiveBuyWeeks,
        trend,
        // Signal flags
        buyingAtLowPrice,
        hasLargePosition,
        isConsistentBuyer,
        positionPct: Math.round(positionPct * 1000) / 10,
      };
    });

    positions.sort((a, b) => b.netPosition - a.netPosition);

    // ── Compute stock-level signals from full history ──
    const instPositions = positions.filter(p => p.isInstitutional);
    const instAccumulators = instPositions.filter(p => p.netPosition > 0);
    const instDistributors = instPositions.filter(p => p.netPosition < 0);

    // Signal A: ANY institution has large volume accumulated at avg price below current
    const instWithLargeLowCost = instPositions.filter(p =>
      p.hasLargePosition && p.buyingAtLowPrice
    );
    const largeLowCostSignal = instWithLargeLowCost.length > 0;

    // Signal B: 3+ institutions are net accumulators across the full uploaded history
    const instConsistentBuyers = instPositions.filter(p => p.isConsistentBuyer);
    const multiInstBuySignal = instConsistentBuyers.length >= 3;

    // Signal strength: composite score
    let signalStrength = 0;
    if (largeLowCostSignal) signalStrength += 2;
    if (multiInstBuySignal) signalStrength += 2;
    signalStrength += Math.min(instConsistentBuyers.length, 5) * 0.4;
    signalStrength += Math.min(instWithLargeLowCost.length, 3) * 0.5;

    results[symbol] = {
      symbol,
      currentPrice,
      firstDate,
      lastDate,
      totalDays: allDates.length,
      positions,
      // Counts
      instPositions: instPositions.length,
      instAccumulators: instAccumulators.length,
      instDistributors: instDistributors.length,
      instWithLargeLowCost: instWithLargeLowCost.length,
      instConsistentBuyers: instConsistentBuyers.length,
      // Signals
      largeLowCostSignal,
      multiInstBuySignal,
      signalStrength,
      // Summary stats
      totalInstNetPosition: instPositions.reduce((s, p) => s + p.netPosition, 0),
      avgInstBuyPrice: weightedAvgBuyPrice(instAccumulators),
    };
  }

  return results;
}

function getBrokerType(broker, brokerTags) {
  if (brokerTags[broker]) return brokerTags[broker];
  const lower = broker.toLowerCase();
  for (const [name, type] of Object.entries(brokerTags)) {
    if (lower.includes(name.toLowerCase()) || name.toLowerCase().includes(lower)) return type;
  }
  return 'OTHER';
}

function weightedAvgBuyPrice(accumulators) {
  const totalVol = accumulators.reduce((s, p) => s + p.totalBuyVol, 0);
  const totalAmt = accumulators.reduce((s, p) => s + p.totalBuyAmt, 0);
  return totalVol > 0 ? Math.round((totalAmt / totalVol) * 10000) / 10000 : 0;
}

function computeWeeklyNet(dailyNet, allDates) {
  if (!allDates.length) return [];

  const weeks = [];
  let weekStart = allDates[0];
  let weekNet = 0;
  let weekLabel = 1;

  allDates.forEach((date, i) => {
    const daysDiff = (new Date(date) - new Date(weekStart)) / (1000 * 60 * 60 * 24);
    if (daysDiff >= 7 && i > 0) {
      weeks.push({ week: `W${weekLabel}`, net: weekNet, start: weekStart, end: allDates[i - 1] });
      weekStart = date;
      weekNet = 0;
      weekLabel++;
    }
    weekNet += dailyNet[date] || 0;
  });

  if (weekNet !== 0 || weeks.length === 0) {
    weeks.push({ week: `W${weekLabel}`, net: weekNet, start: weekStart, end: allDates[allDates.length - 1] });
  }

  return weeks;
}

function countConsecutiveEnd(weeklyNet, type) {
  let count = 0;
  for (let i = weeklyNet.length - 1; i >= 0; i--) {
    if (type === 'buy' && weeklyNet[i].net > 0) count++;
    else if (type === 'sell' && weeklyNet[i].net < 0) count++;
    else break;
  }
  return count;
}
